'use client'

import React from 'react'
import Link from 'next/link'
import { CookieSettingsLink } from '@/components/ConsentBanner'
import { Button } from '@/components/ui/Button'
import { MarketingHeader } from '@/components/MarketingHeader'
import type { BlogPost } from '../posts'

interface Props {
  post: BlogPost
  /** Other guides on the same topic, linked at the end of the article. */
  related: Pick<BlogPost, 'slug' | 'title' | 'description'>[]
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

function renderInlineFormatting(text: string): React.ReactNode {
  // Match bold **text** and markdown links [text](url)
  const regex = /(\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\))/g
  const result: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index))
    }

    if (match[2]) {
      // Bold match
      result.push(<strong key={`b-${match.index}`}>{match[2]}</strong>)
    } else if (match[3] && match[4]) {
      // Link match
      const isExternal = match[4].startsWith('http')
      result.push(
        isExternal ? (
          <a
            key={`a-${match.index}`}
            href={match[4]}
            className="text-primary-600 font-semibold hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {match[3]}
          </a>
        ) : (
          <Link
            key={`a-${match.index}`}
            href={match[4]}
            className="text-primary-600 font-semibold hover:underline"
          >
            {match[3]}
          </Link>
        )
      )
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex))
  }

  return result.length === 1 ? result[0] : result
}

const CHROME_EXT = 'https://chromewebstore.google.com/detail/cvcircuit/oajodloniilndmkhndlmdhdcbdolmoln'

function getCtaForSlug(slug: string): { heading: string; body: string; btnLabel: string; href: string; external?: boolean } {
  if (slug.startsWith('feature-cv-builder-'))
    return { heading: 'Build your CV free with CVCircuit', body: 'Create an ATS-friendly CV in minutes — no design skills needed. CVCircuit writes, formats, and exports it for you.', btnLabel: 'Build My CV Free', href: '/cv/start' }
  if (slug.startsWith('feature-cv-tailoring-'))
    return { heading: 'Build your CV free — then tailor it to any job', body: 'Your base CV is the starting point. Once it\'s built in CVCircuit, you can tailor it to any job description in under 60 seconds.', btnLabel: 'Build My CV Free — Then Start Tailoring', href: '/cv/start' }
  if (slug.startsWith('feature-ats-checker-'))
    return { heading: 'Check your CV\'s ATS score free', body: 'Upload your CV and get an instant ATS compatibility score — no account needed. See exactly what\'s holding your CV back.', btnLabel: 'Check My ATS Score Free', href: '/ats-resume-checker' }
  if (slug.startsWith('feature-cover-letter-'))
    return { heading: 'Build your CV free — then generate cover letters instantly', body: 'Your CV is the foundation. Build it in CVCircuit and unlock tailored cover letters for every job you apply for.', btnLabel: 'Build My CV Free — Then Generate Cover Letters', href: '/cv/start' }
  if (slug.startsWith('feature-job-tracker-'))
    return { heading: 'Build your CV free — then track every application', body: 'CVCircuit\'s job tracker lives alongside your CV and cover letters. Build your CV free and get full pipeline visibility from day one.', btnLabel: 'Build My CV Free — Then Start Tracking', href: '/cv/start' }
  if (slug.startsWith('feature-linkedin-rewrite-'))
    return { heading: 'Build your CV free — then rewrite your LinkedIn profile', body: 'Your LinkedIn rewrite is generated from your CVCircuit CV. Build your CV free first — your LinkedIn rewrite is ready the moment you are.', btnLabel: 'Build My CV Free — Then Rewrite My LinkedIn', href: '/cv/start' }
  if (slug.startsWith('feature-interview-prep-'))
    return { heading: 'Build your CV free — then prep for every interview', body: 'CVCircuit generates tailored interview questions from the job description and pairs them with your CV. Build free and walk into every interview prepared.', btnLabel: 'Build My CV Free — Then Prep for Interviews', href: '/cv/start' }
  if (slug.startsWith('feature-outreach-'))
    return { heading: 'Build your CV free — then start networking', body: 'CVCircuit generates personalised outreach messages from your CV — cold emails, LinkedIn messages, referral requests. Build your CV free and start getting replies.', btnLabel: 'Build My CV Free — Then Start Networking', href: '/cv/start' }
  // Every other guide: the extension, as positioned for long UK application
  // forms (claims checked against the platforms it was tested on).
  return { heading: 'Spend less time on long application forms', body: 'The free CVCircuit Chrome extension fills application forms on NHS Jobs, Trac, Civil Service Jobs, Workday and more from your Career Bank, tailors your CV to each job and flags listings that look like ghost jobs.', btnLabel: 'Add CVCircuit to Chrome — Free', href: CHROME_EXT, external: true }
}

// Client wrapper for a blog post page — page.tsx stays a server component
// purely so it can keep exporting `generateStaticParams`/`generateMetadata`.
// Split out here so a logged-in visitor gets the same AppHeader every other
// in-app page uses, via MarketingHeader.
export default function BlogPostPageClient({ post, related }: Props) {
  const cta = getCtaForSlug(post.slug)

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <MarketingHeader showMenuButton={false} />

      <div className="flex flex-1 relative z-10 lg:mt-8">
        <div className="flex-1 min-w-0 bg-white">
          <main className="container mx-auto px-4 py-12 sm:py-16">
        <article className="max-w-3xl mx-auto">
          <Link
            href="/blog"
            className="text-sm font-semibold text-primary-600 hover:underline"
          >
            &larr; Back to Blog
          </Link>

          <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-gray-900">
            {post.title}
          </h1>

          <div className="mt-3 flex items-center gap-3 text-sm text-gray-500">
            <time dateTime={post.updated ?? post.date}>
              {post.updated ? `Updated ${formatDate(post.updated)}` : formatDate(post.date)}
            </time>
            <span>&middot;</span>
            <span className="text-primary-600 font-semibold">{post.author}</span>
          </div>

          <div className="mt-8 prose prose-gray prose-lg max-w-none prose-a:text-primary-600 prose-a:font-semibold">
            {(() => {
              const lines = post.content.split('\n')
              const elements: React.ReactNode[] = []
              let i = 0

              while (i < lines.length) {
                const trimmed = lines[i].trim()

                if (!trimmed) {
                  i++
                  continue
                }

                // Skip horizontal rules
                if (/^---+$/.test(trimmed)) {
                  i++
                  continue
                }

                // Blockquotes — render as a styled pull-quote
                if (trimmed.startsWith('> ')) {
                  const quoteLines: string[] = []
                  const startIndex = i
                  while (i < lines.length && lines[i].trim().startsWith('> ')) {
                    quoteLines.push(lines[i].trim().slice(2))
                    i++
                  }
                  elements.push(
                    <blockquote key={`bq-${startIndex}`} className="my-4 border-l-4 border-primary-300 pl-4 italic text-gray-600">
                      {quoteLines.map((ql, qi) => (
                        <p key={qi} className="mb-1">{renderInlineFormatting(ql)}</p>
                      ))}
                    </blockquote>
                  )
                  continue
                }

                // Skip markdown table separator rows like |---|---|
                if (/^\|[-| :]+\|$/.test(trimmed)) {
                  i++
                  continue
                }

                // Render table rows as a simple definition-style list
                if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
                  const cells = trimmed.slice(1, -1).split('|').map(c => c.trim()).filter(Boolean)
                  // Skip header-like rows that are all dashes
                  if (cells.every(c => /^[-: ]+$/.test(c))) { i++; continue }
                  elements.push(
                    <div key={i} className="flex gap-4 py-2 border-b border-gray-100 text-sm text-gray-700">
                      {cells.map((cell, ci) => (
                        <span key={ci} className={ci === 0 ? 'font-semibold w-1/3 shrink-0' : 'flex-1'}>
                          {renderInlineFormatting(cell)}
                        </span>
                      ))}
                    </div>
                  )
                  i++
                  continue
                }

                // H1 is already rendered as the page <h1> — skip it in content
                if (trimmed.startsWith('# ') && !trimmed.startsWith('## ')) {
                  i++
                  continue
                }

                if (trimmed.startsWith('### ')) {
                  elements.push(
                    <h3 key={i} className="text-xl font-bold text-gray-900 underline mt-6 mb-3">
                      {trimmed.slice(4)}
                    </h3>
                  )
                  i++
                  continue
                }

                if (trimmed.startsWith('## ')) {
                  elements.push(
                    <h2 key={i} className="text-2xl font-bold text-primary-600 mt-10 mb-4">
                      {trimmed.slice(3)}
                    </h2>
                  )
                  i++
                  continue
                }

                if (trimmed.startsWith('- ')) {
                  const items: React.ReactNode[] = []
                  const startIndex = i
                  while (i < lines.length) {
                    const t = lines[i].trim()
                    if (!t.startsWith('- ')) break
                    const isUnchecked = t.startsWith('- [ ] ')
                    const isChecked   = t.startsWith('- [x] ') || t.startsWith('- [X] ')
                    if (isUnchecked || isChecked) {
                      items.push(
                        <li key={i} className="flex items-start gap-2.5 mb-1.5 text-gray-700 list-none">
                          <span className={[
                            'mt-[3px] flex-shrink-0 w-[17px] h-[17px] rounded-sm border-2 inline-flex items-center justify-center text-[10px] font-bold',
                            isChecked
                              ? 'bg-primary-600 border-primary-600 text-white'
                              : 'border-gray-400',
                          ].join(' ')}>
                            {isChecked ? '✓' : ''}
                          </span>
                          <span>{renderInlineFormatting(t.slice(6))}</span>
                        </li>
                      )
                    } else {
                      const boldMatch = t.match(/^- \*\*(.+?)\*\*\s*[—–-]\s*(.+)$/)
                      if (boldMatch) {
                        items.push(
                          <li key={i} className="mb-2 text-gray-700">
                            <strong>{boldMatch[1]}</strong> — {renderInlineFormatting(boldMatch[2])}
                          </li>
                        )
                      } else {
                        items.push(
                          <li key={i} className="mb-2 text-gray-700">
                            {renderInlineFormatting(t.slice(2))}
                          </li>
                        )
                      }
                    }
                    i++
                  }
                  elements.push(<ul key={`ul-${startIndex}`} className="mb-2 list-disc pl-5">{items}</ul>)
                  continue
                }

                const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/)
                if (numberedMatch) {
                  const items: React.ReactNode[] = []
                  const startIndex = i
                  while (i < lines.length) {
                    const t = lines[i].trim()
                    const nm = t.match(/^(\d+)\.\s+(.+)$/)
                    if (!nm) break
                    items.push(
                      <li key={i} className="mb-2 text-gray-700">
                        {renderInlineFormatting(nm[2])}
                      </li>
                    )
                    i++
                  }
                  elements.push(
                    <ol key={`ol-${startIndex}`} className="list-decimal pl-5">
                      {items}
                    </ol>
                  )
                  continue
                }

                // Image on its own line: ![alt text](path). Deliberately a
                // plain <img>, not next/image — this is a static export (see
                // next.config.js's images.unoptimized) with source images
                // that are already served at their final size, so there's no
                // optimisation step to gain from it. Caption is the alt text,
                // shown under the image like a figure caption — useful for
                // crediting a chart's data source inline.
                if (trimmed.startsWith('![') && trimmed.includes('](')) {
                  const match = trimmed.match(/^!\[(.*?)\]\((.+?)\)$/)
                  if (match) {
                    const [, alt, src] = match
                    elements.push(
                      <figure key={i} className="my-6">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={src} alt={alt} loading="lazy" className="w-full h-auto rounded-xl border border-gray-100" />
                        {alt && <figcaption className="mt-2 text-xs text-gray-400 text-center">{alt}</figcaption>}
                      </figure>
                    )
                    i++
                    continue
                  }
                }

                if (trimmed.startsWith('[') && trimmed.includes('](')) {
                  const match = trimmed.match(/\[(.+?)\]\((.+?)\)/)
                  if (match) {
                    elements.push(
                      <p key={i} className="mt-4">
                        <Link
                          href={match[2]}
                          className="text-primary-600 font-semibold hover:underline"
                        >
                          {match[1]}
                        </Link>
                      </p>
                    )
                    i++
                    continue
                  }
                }

                elements.push(
                  <p key={i} className="mb-4 text-gray-700 leading-relaxed">
                    {renderInlineFormatting(trimmed)}
                  </p>
                )
                i++
              }

              return elements
            })()}
          </div>

          {related.length > 0 && (
            <nav aria-labelledby="related-guides" className="mt-12 border-t border-gray-200 pt-8">
              <h2 id="related-guides" className="text-xl font-bold text-gray-900">Related guides</h2>
              <ul className="mt-4 space-y-4">
                {related.map(r => (
                  <li key={r.slug}>
                    <Link href={`/blog/${r.slug}`} className="font-semibold text-primary-600 hover:underline">
                      {r.title}
                    </Link>
                    <p className="mt-1 text-sm text-gray-500 line-clamp-2">{r.description}</p>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <div className="mt-12 rounded-xl border border-primary-100 bg-primary-50 p-6 text-center">
            <h3 className="text-lg font-semibold text-gray-900">{cta.heading}</h3>
            <p className="mt-2 text-gray-600">{cta.body}</p>
            {cta.external ? (
              <a href={cta.href} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block">
                <Button size="lg">{cta.btnLabel}</Button>
              </a>
            ) : (
              <Link href={cta.href} className="mt-4 inline-block">
                <Button size="lg">{cta.btnLabel}</Button>
              </Link>
            )}
          </div>
        </article>
          </main>

          <footer className="border-t py-8 mt-12">
            <div className="container mx-auto px-4 text-center text-gray-600">
              <p>&copy; 2026 CVCircuit. All rights reserved.</p>
              <div className="mt-3 flex justify-center gap-6 text-sm">
                <Link href="/terms" className="text-primary-600 font-semibold hover:underline">
                  Terms
                </Link>
                <Link href="/privacy" className="text-primary-600 font-semibold hover:underline">
                  Privacy
                </Link>
                <CookieSettingsLink />
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  )
}
